import { ReactNode, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Reveal } from 'shared/components/motion/Reveal';
import { DURATION, EASE_OUT } from 'shared/components/motion/tokens';
import { Card } from 'shared/components/ui/Card';
import { Button } from 'shared/components/ui/Button';
import { downloadCsv } from 'shared/utils/csv';

interface ChartCardProps {
  title: string;
  subtitle?: string;
  tableHeaders: string[];
  tableRows: (string | number)[][];
  children: ReactNode;
}

// Todo gráfico desta tela ganha uma visão em tabela equivalente: nenhuma informação
// fica disponível só por cor (requisito de acessibilidade do skill de dataviz).
export function ChartCard({ title, subtitle, tableHeaders, tableRows, children }: ChartCardProps) {
  const [showTable, setShowTable] = useState(false);

  return (
    <Reveal className="h-full">
      <Card className="h-full">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading font-semibold text-ink">{title}</h3>
            {subtitle && <p className="text-sm text-ink-secondary">{subtitle}</p>}
          </div>
          <div className="flex flex-shrink-0 gap-2 print:hidden">
            <Button
              variant="ghost"
              className="px-3 py-1 text-xs"
              onClick={() => downloadCsv(title, tableHeaders, tableRows)}
              title="Baixar os dados deste gráfico em CSV"
            >
              CSV
            </Button>
            <Button variant="ghost" className="px-3 py-1 text-xs" onClick={() => setShowTable((prev) => !prev)}>
              {showTable ? 'Ver gráfico' : 'Ver tabela'}
            </Button>
          </div>
        </div>

        <div className="mt-4">
          <AnimatePresence mode="wait" initial={false}>
            <motion.div
              key={showTable ? 'table' : 'chart'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: DURATION.fast, ease: EASE_OUT }}
            >
              {showTable ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border/70 text-left text-xs uppercase tracking-wide text-ink-secondary">
                        {tableHeaders.map((header) => (
                          <th key={header} className="py-2 pr-4 font-medium">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tableRows.map((row, rowIndex) => (
                        <tr
                          key={rowIndex}
                          className="border-b border-border/50 transition-colors last:border-0 hover:bg-accent-soft/40"
                        >
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="py-2 pr-4 text-ink">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                children
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </Card>
    </Reveal>
  );
}
